import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Warning, LockSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { t } from "@/lib/i18n";

const AlertCtx = createContext(null);
export const useBillingAlert = () => useContext(AlertCtx);

let cached = null;
let cachedPool = null;
let cachedAt = 0;
export const primeBillingCache = (alert, pool) => { cached = alert; cachedPool = pool; cachedAt = Date.now(); };

export const BillingBanner = () => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(cached);
  const [pool, setPool] = useState(cachedPool);
  useEffect(() => {
    if (Date.now() - cachedAt < 60000) { setAlert(cached); setPool(cachedPool); return; }
    api.get("/billing/alert").then((r) => { cached = r.data; cachedAt = Date.now(); setAlert(r.data); }).catch(() => {});
    api.get("/team/tokens").then((r) => { cachedPool = r.data; setPool(r.data); }).catch(() => {});
  }, []);
  if (!alert?.show) {
    if (pool && !pool.unlimited && pool.pct_used >= 80) {
      const full = pool.exhausted;
      return (
        <div data-testid={full ? "token-exhausted-banner" : "token-warning-banner"}
          className={`w-full ${full ? "bg-red-600 text-white" : "bg-amber-100 text-amber-900"} text-[13px] font-medium px-4 py-2 flex items-center justify-center gap-2 shrink-0`}>
          <Warning size={15} weight="bold" className="shrink-0" />
          <span>
            {full ? "Token pool exhausted — AI features are paused." : `Your team has used ${pool.pct_used}% of this month's tokens.`}{" "}
            {pool.is_payer ? (
              <button data-testid="token-banner-buy-link" onClick={() => navigate("/billing")} className="underline font-bold">{t("billing.buy_tokens", "Buy more tokens")}</button>
            ) : (
              <span className="font-bold">Contact your team admin ({pool.payer_email})</span>
            )}
          </span>
        </div>
      );
    }
    return null;
  }

  if (alert.state === "locked") {
    return (
      <div data-testid="billing-lockout" className="fixed inset-0 z-[100] bg-white/95 backdrop-blur-sm flex items-center justify-center">
        <div className="max-w-md text-center px-8">
          <LockSimple size={44} className="mx-auto text-red-600 mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">{t("billing.expired", "Your subscription has expired")}</h2>
          <p className="text-sm text-gray-600 mb-6">
            {alert.is_payer
              ? "Renew your subscription to continue using Agentic Inbox. Your data is safe and will be right here when you return."
              : `Your team's Agentic Inbox subscription has expired. Please contact your team admin (${alert.payer_email}) to renew.`}
          </p>
          {alert.is_payer && (
            <button data-testid="lockout-renew-btn" onClick={() => navigate("/billing")}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md px-6 py-2.5 text-sm transition-colors">
              Renew subscription
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="billing-grace-banner"
      className="w-full bg-red-600 text-white text-[13px] font-medium px-4 py-2 flex items-center justify-center gap-2 shrink-0">
      <Warning size={15} weight="bold" className="shrink-0" />
      <span>
        We are unable to renew your Agentic Inbox subscription.{" "}
        {alert.is_payer ? (
          <button data-testid="billing-banner-link" onClick={() => navigate("/billing")}
            className="underline font-bold hover:text-red-100 transition-colors">
            Please update your payment information
          </button>
        ) : (
          <span className="font-bold">Please ask your team admin ({alert.payer_email}) to update the payment information</span>
        )}{" "}
        by <b>{alert.deadline}</b> or you will not be able to use Agentic Inbox.
      </span>
    </div>
  );
};
